/**
 * ProgressVisualizer Component
 * 
 * Visualizes progress through a sequence of moves or steps with multiple display variants.
 */

import React from 'react';
import styles from './ProgressVisualizer.module.css';

/**
 * Props for the ProgressVisualizer component
 */
export interface ProgressVisualizerProps {
  /** Total number of moves/steps in the sequence */
  totalMoves: number;
  /** Number of moves/steps completed so far */
  completedMoves: number;
  /** Optional percentage of correct first attempts (0-100) */
  accuracy?: number;
  /** Visual display style for the progress */
  variant?: 'bar' | 'steps' | 'timeline';
  /** Optional class name for custom styling */
  className?: string;
}

/**
 * Memoized ProgressVisualizer Component
 * 
 * Displays progress through a sequence of moves using different visualization methods:
 * - Bar: Linear progress bar with percentage
 * - Steps: Individual step indicators showing completed/current/remaining moves
 * - Timeline: Horizontal timeline with move notation markers
 */
const MemoizedProgressVisualizer: React.FC<ProgressVisualizerProps> = ({
  totalMoves,
  completedMoves,
  accuracy,
  variant = 'bar',
  className,
}) => {
  // Ensure values are valid
  const validatedTotal = Math.max(1, totalMoves);
  const validatedCompleted = Math.max(0, Math.min(completedMoves, validatedTotal));
  
  // Calculate progress percentage
  const progressPercentage = Math.round((validatedCompleted / validatedTotal) * 100);
  
  /**
   * Renders the bar variant
   */
  const renderProgressBar = () => {
    return (
      <div className={styles.barContainer} data-testid="progress-bar">
        <div 
          className={styles.barProgress} 
          style={{ width: `${progressPercentage}%` }}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progressPercentage}
          role="progressbar"
        ></div>
        <div className={styles.barInfo}>
          <span className={styles.progressPercentage}>{progressPercentage}%</span>
          <span className={styles.fractionText}>
            {validatedCompleted} / {validatedTotal}
          </span>
          {accuracy !== undefined && (
            <span className={styles.accuracyText}>
              Accuracy: {Math.round(accuracy)}%
            </span>
          )}
        </div>
      </div>
    );
  };
  
  /**
   * Renders the steps variant with individual indicators
   */
  const renderSteps = () => {
    // Generate array of step indicators
    const steps = Array.from({ length: validatedTotal }, (_, index) => {
      let stepClass = styles.stepIncomplete;
      
      if (index < validatedCompleted) {
        stepClass = styles.stepComplete;
      } else if (index === validatedCompleted) {
        stepClass = styles.stepCurrent;
      }
      
      return (
        <div 
          key={`step-${index}`}
          className={`${styles.step} ${stepClass}`}
          aria-label={
            index < validatedCompleted 
              ? `Step ${index + 1} complete` 
              : index === validatedCompleted 
                ? `Current step ${index + 1}` 
                : `Step ${index + 1} incomplete`
          }
        >
          <span className={styles.stepNumber}>{index + 1}</span>
        </div>
      );
    });
    
    return (
      <div className={styles.stepsContainer} data-testid="progress-steps">
        <div className={styles.stepsTrack}>
          {steps}
        </div>
        {accuracy !== undefined && (
          <div className={styles.stepsInfo}>
            <span className={styles.accuracyText}>
              Accuracy: {Math.round(accuracy)}%
            </span>
          </div>
        )}
      </div>
    );
  };
  
  /**
   * Renders the timeline variant with move markers
   */
  const renderTimeline = () => {
    // Generate timeline markers
    const markers = Array.from({ length: validatedTotal }, (_, index) => {
      let markerClass = styles.markerIncomplete;
      
      if (index < validatedCompleted) {
        markerClass = styles.markerComplete;
      } else if (index === validatedCompleted) {
        markerClass = styles.markerCurrent;
      }
      
      return (
        <div 
          key={`marker-${index}`}
          className={`${styles.timelineMarker} ${markerClass}`}
          aria-label={
            index < validatedCompleted 
              ? `Move ${index + 1} complete` 
              : index === validatedCompleted 
                ? `Current move ${index + 1}` 
                : `Move ${index + 1} incomplete`
          }
        >
          <div className={styles.markerDot}></div>
          <span className={styles.markerLabel}>{index + 1}</span>
        </div>
      );
    });
    
    // Calculate progress line width
    const progressWidth = validatedCompleted === validatedTotal 
      ? '100%' 
      : `calc(${(validatedCompleted / validatedTotal) * 100}% ${validatedCompleted > 0 ? '+ 10px' : ''})`;
    
    return (
      <div className={styles.timelineContainer} data-testid="progress-timeline">
        <div className={styles.timelineLine}>
          <div 
            className={styles.timelineProgress} 
            style={{ width: progressWidth }}
          ></div>
        </div>
        <div className={styles.timelineMarkers}>
          {markers}
        </div>
        <div className={styles.timelineInfo}>
          {accuracy !== undefined && (
            <span className={styles.accuracyText}>
              Accuracy: {Math.round(accuracy)}%
            </span>
          )}
        </div>
      </div>
    );
  };
  
  // Render the appropriate variant
  const renderVariant = () => {
    switch (variant) {
      case 'steps':
        return renderSteps();
      case 'timeline':
        return renderTimeline();
      case 'bar':
      default:
        return renderProgressBar();
    }
  };
  
  return (
    <div 
      className={`${styles.container} ${className || ''}`}
      data-testid="progress-visualizer"
    >
      {renderVariant()}
    </div>
  );
};

export default React.memo(MemoizedProgressVisualizer); 